import { ImageShape, ProductState } from '@/types';
import Link from '@/components/shared/Link';
import Button from '../Button';
import Slider from './Slider';
import useIsomorphicLayoutEffect from '@/hooks/use-isomorphic-layout-effect';
import { HTMLAttributes, useRef, useState } from 'react';
import Responsive from '../Responsive';
import Image from 'next/legacy/image';
import { toCurrency } from '@/utils/to-currency';
import classNames from 'classnames';
import { useRoubleToDollarCourseState } from '@/atoms/rouble-to-dollar-course';
import dynamic from 'next/dynamic';
import { CategoryId } from '@/api/categories';
import { CATEGORIES_IDS } from '@/api/categories';
import { fromCDN } from '@/cdn';
import { useTypograf } from '@/hooks/use-typograf';
import { stateDict } from '@/dicts';
import ThumbsSlider from '../ThumbsSlider';
import { ThumbsProvider } from '../ThumbsSlider/ThumbsSlider';
import { useEuroToDollarCourseState } from '@/atoms/euro-to-dollar-course';

const CompareButton = dynamic(() => import('./CompareButton'), { ssr: false });
const FavoriteButton = dynamic(() => import('./FavoriteButton'), { ssr: false });
export interface Props extends HTMLAttributes<HTMLElement> {
    productId: number;
    slug: string;
    cardName: string;
    price: number;
    materialName: string;
    state: ProductState;
    available: boolean | null;
    reference?: string | null;
    diameter?: string | null;
    isStandardForm?: boolean;
    accessories?: string | null;
    productColor?: string | null;
    type?: string | null;
    params?: string | null;
    insert?: string | null;
    size?: string | null;
    images: ImageShape[];
    variant?: 'default' | 'index';
    width?: 'default' | 'wide';
    categoryId?: CategoryId;
}

const watchCharacteristics = ['reference', 'diameter', 'materialName'];
const bagCharacteristics = ['diameter', 'materialName', 'accessories', 'color', 'size'];
const decorationCharacteristics = ['reference', 'materialName', 'type', 'insert'];
const accessoryCharacteristics = ['reference', 'materialName', 'type', 'insert'];
const heartsCharacteristics = ['categoryName', 'materialName'];

const characteristics = {
    [CATEGORIES_IDS.watch]: watchCharacteristics,
    [CATEGORIES_IDS.bag]: bagCharacteristics,
    [CATEGORIES_IDS.decoration]: decorationCharacteristics,
    [CATEGORIES_IDS.accessory]: accessoryCharacteristics,
    [CATEGORIES_IDS['chrome-hearts']]: heartsCharacteristics,
};

type CharacteristicsObj = Partial<Record<string, string | number | undefined>>;

function filterObject(obj: CharacteristicsObj, allowedKeys: string[]): CharacteristicsObj {
    const filteredObject: CharacteristicsObj = {};
    allowedKeys.forEach((key) => {
        if (obj.hasOwnProperty(key)) {
            filteredObject[key] = obj[key];
        }
    });
    return filteredObject;
}

const Card = ({
    productId,
    slug,
    cardName,
    price,
    materialName,
    size,
    state,
    available,
    reference,
    diameter,
    accessories,
    productColor,
    type,
    params,
    insert,
    images,
    variant = 'default',
    width = 'default',
    categoryId,
    isStandardForm = true,
    ...props
}: Props) => {
    const categoryNames = {
        [CATEGORIES_IDS.watch]: 'Часы',
        [CATEGORIES_IDS.bag]: 'Сумки',
        [CATEGORIES_IDS.decoration]: 'Украшения',
        [CATEGORIES_IDS.accessory]: 'Аксессуары',
        [CATEGORIES_IDS['chrome-hearts']]: 'Chrome Hearts',
    };

    const partialCharacteristics: CharacteristicsObj = {
        reference: reference ?? undefined,
        diameter: diameter ?? undefined,
        materialName,
        accessories: accessories ?? undefined,
        color: productColor ?? undefined,
        type: type ?? undefined,
        params: params ?? undefined,
        insert: insert ?? undefined,
        size: size ?? undefined,
        categoryName: categoryId ? categoryNames[categoryId] : undefined,
    };
    const tpCardName = useTypograf(cardName);
    const filteredCharacteristics = categoryId
        ? filterObject(partialCharacteristics, characteristics[categoryId])
        : partialCharacteristics;

    const cardRef = useRef<HTMLAnchorElement>(null);
    const centerRef = useRef<HTMLDivElement>(null);
    const [spaceBetween, setSpaceBetween] = useState(40);
    const [course] = useRoubleToDollarCourseState();
    const [euroCourse] = useEuroToDollarCourseState();

    const partialCharacteristicsDict: Record<string, string> = {
        reference: 'Ref',
        diameter: categoryId === CATEGORIES_IDS['bag'] ? 'Размер' : 'Диаметр',
        materialName: 'Материал',
        accessories: 'Цвет фурнитуры',
        color: 'Цвет',
        type: 'Тип',
        params: 'Параметры',
        insert: 'Вставка',
        size: 'Размер',
        categoryName: 'Категория',
    };

    useIsomorphicLayoutEffect(() => {
        const onResize = () => {
            if (cardRef.current && centerRef.current) {
                setSpaceBetween(
                    cardRef.current.getBoundingClientRect().width - centerRef.current.getBoundingClientRect().width,
                );
            }
        };

        window.addEventListener('resize', onResize);
        onResize();

        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, []);

    return (
        <ThumbsProvider>
            <Link
                {...props}
                href={`/catalog/${productId}`}
                className={classNames(`card card-${variant} card-width-${width}`, props.className)}
                ref={cardRef}
                itemScope
                itemType="http://schema.org/Product"
            >
                <div className="card-header">
                    <div className="card-state">
                        <Button geometryVariant="mustache" disabled size="sm" tag="div" visuallyDisabled={true}>
                            {stateDict[state]}
                        </Button>
                    </div>
                    {variant === 'index' && (
                        <>
                            <div className="card-name m-text-xs" itemProp="name">
                                {tpCardName}
                            </div>
                            {price !== 0 ? (
                                <div
                                    className="card-price m-text-xs--add"
                                    itemProp="offers"
                                    itemScope
                                    itemType="http://schema.org/Offer"
                                >
                                    <div className="card-price--dollar" itemProp="price">
                                        {toCurrency(price, 'USD')}
                                        <meta itemProp="priceCurrency" content="USD" />
                                    </div>
                                    {course && course !== 0 && (
                                        <>
                                            <div className="price--delimeter">/&nbsp;</div>
                                            <div className="price--rouble" itemProp="price">
                                                {toCurrency(price * course, 'RUB')}
                                                <meta itemProp="priceCurrency" content="RUB" />
                                            </div>
                                        </>
                                    )}
                                </div>
                            ) : (
                                <div className="card-price zero-price m-text-xs">под заказ</div>
                            )}
                        </>
                    )}
                    {variant === 'default' && (
                        <div className="card-favorite">
                            <CompareButton productId={productId} categoryId={categoryId} />
                            <FavoriteButton variant={variant} productId={productId} />
                        </div>
                    )}
                </div>

                <div className="card-center" ref={centerRef}>
                    <Responsive className="card-image">
                        {variant === 'default' ? (
                            <Slider images={images} spaceBetween={spaceBetween} />
                        ) : (
                            images[0] && (
                                <Image
                                    loader={({ src, width, quality }) =>
                                        fromCDN(
                                            `/nextapi/optimize?src=${encodeURIComponent(src)}&w=${width}&q=${
                                                quality || 80
                                            }`,
                                        )
                                    }
                                    src={images[0].src}
                                    alt={cardName}
                                    sizes="(max-width: 767px) 80.55vw, (max-width: 1200px) 37.76vw, (max-width: 1799px) 20.86vw, 18.75vw"
                                    itemProp="image"
                                    layout="fill"
                                />
                            )
                        )}
                    </Responsive>
                </div>

                <ThumbsSlider imagesCount={images.length} />

                <div className="card-footer">
                    {variant === 'default' && (
                        <div className="card-name m-text-xs" itemProp="name">
                            {tpCardName}
                        </div>
                    )}
                    {variant === 'default' && (
                        <>
                            {price !== 0 && (
                                <div
                                    className="card-price m-text-xs--add"
                                    itemProp="offers"
                                    itemScope
                                    itemType="http://schema.org/Offer"
                                >
                                    {course && course !== 0 && (
                                        <div className="card-price--main-rouble" itemProp="price">
                                            {toCurrency(price * course, 'RUB')}
                                            <meta itemProp="priceCurrency" content="RUB" />
                                        </div>
                                    )}
                                    <div className="card-price--dollar" itemProp="price">
                                        {toCurrency(price, 'USD')}
                                        <meta itemProp="priceCurrency" content="USD" />
                                    </div>
                                    {course && course !== 0 && (
                                        <>
                                            <div className="price--delimeter">/&nbsp;</div>
                                            <div className="card-price--euro" itemProp="price">
                                                {toCurrency(price * euroCourse, 'EUR')}
                                                <meta itemProp="priceCurrency" content="EUR" />
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                    <ul className="card-characteristics list-unstyled">
                        {Object.keys(filteredCharacteristics).map((key, i) => {
                            if (filteredCharacteristics[key] && filteredCharacteristics[key] !== ' мм') {
                                return (
                                    <li className="card-characteristics-item" key={i}>
                                        <div className="card-characteristics-key m-text-xs--add">
                                            {partialCharacteristicsDict[key]}
                                        </div>
                                        <div
                                            className="card-characteristics-value m-text-xs--add"
                                            dangerouslySetInnerHTML={{
                                                __html: `${filteredCharacteristics[key]}${key === 'diameter' && typeof filteredCharacteristics[key] === 'number' ? ' мм' : ''}`,
                                            }}
                                        ></div>
                                    </li>
                                );
                            }
                        })}
                    </ul>
                    {variant === 'default' && (
                        <div className="card-available">
                            {available ? (
                                <div className="card-available__text m-text-xs">В наличии</div>
                            ) : (
                                <div className="card-available__text m-text-xs">Под заказ</div>
                            )}
                        </div>
                    )}
                </div>
                {variant === 'index' && (
                    <div className="card-favorite--index">
                        <FavoriteButton variant={variant} productId={productId} />
                    </div>
                )}
            </Link>
        </ThumbsProvider>
    );
};

export default Card;
